import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Outlet, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  FaBars, 
  FaTimes, 
  FaSignOutAlt, 
  FaTachometerAlt, 
  FaBox, 
  FaShoppingBag, 
  FaUsers,
  FaChevronLeft,
  FaChevronRight,
  FaChevronDown,
  FaCog,
  FaQuestionCircle,
  FaHome,
  FaChartLine,
  FaShoppingCart,
  FaUserCog,
  FaCubes,
  FaHistory,
  FaLayerGroup,
  FaPalette,
  FaRocket,
  FaShieldAlt,
  FaDatabase,
  FaCloud,
  FaMagic,
  FaStar,
  FaGem,
  FaCrown,
  FaPlus,
  FaChartBar
} from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify'; 
import 'react-toastify/dist/ReactToastify.css'; 

// Constantes para mejor mantenibilidad
const SIDEBAR_CONFIG = {
  EXPANDED: '272px',
  COLLAPSED: '72px',
  MOBILE_CLOSED: '0px',
  MOBILE_EXPANDED: '280px'
};

const THEME = {
  colors: {
    primary: '#0C4B45',
    secondary: '#662D8F',
    accent: '#83F4E9',
    gradient: {
      sidebar: 'from-[#0C4B45] to-[#083D38]',
      purple: 'from-[#662D8F] to-[#F2A9FD]',
      blue: 'from-blue-500 to-cyan-500',
      green: 'from-green-500 to-emerald-500',
      purplePink: 'from-purple-500 to-pink-500',
      orangeRed: 'from-orange-500 to-red-500',
      indigoPurple: 'from-indigo-500 to-purple-500',
      tealBlue: 'from-teal-500 to-blue-500'
    }
  }
};

const AdminLayout = () => {
  const { user, isAdmin, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isHovering, setIsHovering] = useState(false);
  const [activeItem, setActiveItem] = useState('dashboard');
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('admin-sidebar-collapsed') === 'true');
  const [floatingElements, setFloatingElements] = useState([]);
  
  const location = useLocation();
  const navigate = useNavigate();
  const userMenuRef = useRef(null);

  // Memoized menu items configuration
  const menuItems = useMemo(() => [
    { 
      id: 'dashboard', 
      name: 'Dashboard', 
      icon: <FaTachometerAlt />, 
      path: '/admin/dashboard',
      color: THEME.colors.gradient.blue,
      description: 'Resumen general del sistema'
    },
    { 
      id: 'products', 
      name: 'Productos', 
      icon: <FaBox />, 
      path: '/admin/products',
      color: THEME.colors.gradient.green,
      description: 'Gestión de inventario'
    },
    { 
      id: 'orders', 
      name: 'Órdenes', 
      icon: <FaShoppingCart />, 
      path: '/admin/orders',
      color: THEME.colors.gradient.orangeRed,
      description: 'Gestión de pedidos'
    },
    { 
      id: 'users', 
      name: 'Usuarios', 
      icon: <FaUsers />, 
      path: '/admin/users',
      color: THEME.colors.gradient.indigoPurple,
      description: 'Administración de usuarios'
    },
    {
      id: 'settings',
      name: 'Configuración',
      icon: <FaCog />,
      path: '/admin/settings',
      color: THEME.colors.gradient.purple,
      description: 'Píxel de Meta, aniversario y WhatsApp'
    },
  ], []);

  const quickActions = useMemo(() => [
    { 
      name: 'Nuevo Producto', 
      icon: <FaPlus />, 
      color: 'bg-gradient-to-r from-green-500 to-emerald-500', 
      path: '/admin/products/new' 
    },
  ], []);

  // Floating elements generation
  useEffect(() => {
    const elements = Array.from({ length: 20 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      delay: Math.random() * 5,
      duration: 4 + Math.random() * 6,
      size: 3 + Math.random() * 4,
      color: Math.random() > 0.5 ? 'from-[#0C4B45]/20 to-[#083D38]/20' : 'from-[#662D8F]/20 to-[#512577]/20'
    }));
    setFloatingElements(elements);
  }, []);

  useEffect(() => {
    localStorage.setItem('admin-sidebar-collapsed', String(sidebarCollapsed));
  }, [sidebarCollapsed]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      setUserMenuOpen(false);
      if (isMobile) setSidebarOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isMobile]);

  // Responsive handling
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      
      if (mobile) {
        setSidebarOpen(false);
        setSidebarCollapsed(false);
      } else {
        setSidebarOpen(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Click outside handlers
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close sidebar on mobile navigation
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [location, isMobile]);

  // Active item determination
  useEffect(() => {
    const path = location.pathname;
    const matchedItem = menuItems.find(item => path === item.path || path.startsWith(`${item.path}/`));
    setActiveItem(matchedItem?.id || 'dashboard');
  }, [location.pathname, menuItems]);

  // Sidebar width calculation
  const getSidebarWidth = useCallback(() => {
    if (isMobile) {
      return sidebarOpen ? SIDEBAR_CONFIG.MOBILE_EXPANDED : SIDEBAR_CONFIG.MOBILE_CLOSED;
    }
    if (sidebarCollapsed) return SIDEBAR_CONFIG.COLLAPSED;
    return sidebarOpen ? SIDEBAR_CONFIG.EXPANDED : isHovering ? SIDEBAR_CONFIG.EXPANDED : SIDEBAR_CONFIG.COLLAPSED;
  }, [isMobile, sidebarOpen, sidebarCollapsed, isHovering]);

  // Content margin calculation
  const getContentMargin = useCallback(() => {
    if (isMobile) return '0px';
    if (sidebarCollapsed) return SIDEBAR_CONFIG.COLLAPSED;
    return sidebarOpen ? SIDEBAR_CONFIG.EXPANDED : SIDEBAR_CONFIG.COLLAPSED;
  }, [isMobile, sidebarOpen, sidebarCollapsed]);

  // Logout handler
  const handleLogout = useCallback(() => {
    toast.success(
      <motion.div 
        initial={{ scale: 0.8 }}
        animate={{ scale: 1 }}
        className="flex items-center"
      >
        <FaSignOutAlt className="mr-2 text-[#662D8F]" />
        <span>Sesión cerrada correctamente</span>
      </motion.div>,
      {
        position: "top-right",
        className: 'bg-white border-l-4 border-[#662D8F] shadow-2xl',
        progressStyle: { background: 'linear-gradient(to right, #662D8F, #512577)' }
      }
    );
    logout();
    setUserMenuOpen(false);
    if (isMobile) setSidebarOpen(false);
    navigate('/login', { replace: true });
  }, [logout, isMobile, navigate]);

  // Sidebar toggle handler
  const handleSidebarToggle = useCallback(() => {
    if (isMobile) {
      setSidebarOpen(prev => !prev);
    } else {
      setSidebarCollapsed(prev => !prev);
    }
  }, [isMobile]);

  // Current active item name
  const activeItemName = useMemo(() => 
    menuItems.find(item => item.id === activeItem)?.name || 'Dashboard',
    [menuItems, activeItem]
  );

  if (!user || isAdmin !== true) {
    return <Navigate to="/" replace />;
  }

  // User initial for avatar
  const userInitial = user?.name?.charAt(0).toUpperCase() || 'U';

  return (
    <div className="flex h-screen bg-gradient-to-br from-gray-50 via-white to-[#0C4B45]/5 overflow-hidden relative">
      {/* Floating Background Elements */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        {floatingElements.map((element) => (
          <motion.div
            key={element.id}
            className={`absolute rounded-full bg-gradient-to-r ${element.color}`}
            style={{
              width: element.size,
              height: element.size,
              left: `${element.x}%`,
              top: `${element.y}%`,
            }}
            animate={{
              y: [0, -50, 0],
              x: [0, 20, 0],
              opacity: [0, 0.8, 0],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: element.duration,
              delay: element.delay,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>

      {/* Mobile Overlay */}
      <AnimatePresence>
        {isMobile && sidebarOpen && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar Component - CORREGIDO: no scroll interno */}
      <motion.div 
        className="bg-gradient-to-b from-[#0C4B45] to-[#083D38] text-white h-screen flex flex-col fixed z-50 shadow-xl border-r border-white/10"
        style={{ width: getSidebarWidth() }}
        initial={false}
        animate={{ 
          width: getSidebarWidth(),
          x: isMobile && !sidebarOpen ? -280 : 0
        }}
        transition={{ type: "spring", damping: 30, stiffness: 200 }}
        onMouseEnter={() => !isMobile && !sidebarCollapsed && setIsHovering(true)}
        onMouseLeave={() => !isMobile && !sidebarCollapsed && setIsHovering(false)}
      >
        {/* Logo Section */}
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="flex items-center"
          >
            {(sidebarOpen || isHovering || isMobile || sidebarCollapsed) ? (
              <motion.div
                whileHover={{ scale: 1.05, rotate: 5 }}
                className="flex items-center"
              >
                <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-[#662D8F] to-[#F2A9FD] flex items-center justify-center mr-3 shadow-md relative overflow-hidden">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"
                  />
                  <FaGem className="text-white text-lg relative z-10" />
                </div>
                {(sidebarOpen || isHovering || isMobile) && !sidebarCollapsed && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.1 }}
                  >
                    <h1 className="text-lg font-bold bg-gradient-to-r from-white to-[#83F4E9] bg-clip-text text-transparent">
                      AdminPcVasquez
                    </h1>
                    <p className="text-xs text-[#83F4E9]/80">Panel de Control</p>
                  </motion.div>
                )}
              </motion.div>
            ) : (
              <motion.div
                whileHover={{ scale: 1.1 }}
                className="w-10 h-10 rounded-xl bg-gradient-to-r from-[#662D8F] to-[#F2A9FD] flex items-center justify-center mx-auto shadow-md"
              >
                <FaCrown className="text-white text-lg" />
              </motion.div>
            )}
          </motion.div>
          
          {!isMobile && (sidebarOpen || isHovering) && !sidebarCollapsed && (
            <motion.button 
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="text-[#83F4E9] hover:text-white ml-2 transition-all p-2 rounded-xl hover:bg-[#83F4E9]/10 backdrop-blur-sm"
              whileHover={{ scale: 1.1, rotate: 180 }}
              whileTap={{ scale: 0.9 }}
            >
              <FaChevronLeft size={16} />
            </motion.button>
          )}
        </div>
        
        {/* User Profile Section */}
        <div className="p-4 border-b border-white/10">
          {(sidebarOpen || isHovering || isMobile || sidebarCollapsed) && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="flex items-center"
            >
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="relative"
              >
                <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-[#662D8F] to-[#F2A9FD] flex items-center justify-center text-white font-bold text-base shadow-md relative overflow-hidden">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                  />
                  <span className="relative z-10">{userInitial}</span>
                </div>
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-[#0C4B45]"
                />
              </motion.div>
              {(sidebarOpen || isHovering || isMobile) && !sidebarCollapsed && (
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                  className="ml-3 flex-1"
                >
                  <p className="text-sm font-semibold text-white truncate">{user?.name || 'Usuario'}</p>
                  <p className="text-xs text-[#83F4E9] flex items-center">
                    <FaShieldAlt className="mr-1" size={12} />
                    Administrador
                  </p>
                  <div className="w-full bg-[#83F4E9]/20 rounded-full h-1 mt-1.5">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: '85%' }}
                      transition={{ delay: 0.5, duration: 1 }}
                      className="bg-gradient-to-r from-[#83F4E9] to-[#662D8F] h-1 rounded-full"
                    />
                  </div>
                </motion.div>
              )}
            </motion.div>
          )}
        </div>
        
        {/* Navigation Menu - CORREGIDO: height fijo con overflow */}
        <div className="mt-3 flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar" style={{ maxHeight: 'calc(100vh - 248px)' }}>
          <div className="px-4 mb-2">
            {(sidebarOpen || isHovering || isMobile) && !sidebarCollapsed && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-xs uppercase text-[#83F4E9]/60 font-semibold tracking-wider"
              >
                Navegación Principal
              </motion.p>
            )}
          </div>
          
          {menuItems.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Link 
                to={item.path} 
                className={`relative flex items-center mx-2 mb-1 py-2.5 px-3 rounded-xl transition-all duration-300 group overflow-hidden ${
                  activeItem === item.id 
                    ? 'bg-gradient-to-r from-[#83F4E9]/20 to-[#662D8F]/20 shadow-md ring-1 ring-[#83F4E9]/25' 
                    : 'hover:bg-[#83F4E9]/10'
                }`}
                onClick={() => setActiveItem(item.id)}
                aria-current={activeItem === item.id ? 'page' : undefined}
              >
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  className={`w-9 h-9 rounded-lg bg-gradient-to-r ${item.color} flex items-center justify-center text-white shadow-sm relative overflow-hidden`}
                >
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"
                  />
                  <div className="relative z-10">
                    {item.icon}
                  </div>
                </motion.div>
                
                {(sidebarOpen || isHovering || isMobile) && !sidebarCollapsed && (
                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="ml-3 flex-1 min-w-0"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-white font-medium text-sm truncate">{item.name}</span>
                    </div>
                    <p className="text-[#83F4E9]/70 text-xs mt-1 truncate">{item.description}</p>
                  </motion.div>
                )}

                <motion.div
                  initial={{ opacity: 0 }}
                  whileHover={{ opacity: 1 }}
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent"
                />
              </Link>
            </motion.div>
          ))}
        </div>
        
        
    
        {/* Logout Section */}
        <div className="p-3 border-t border-white/10">
          <motion.button 
            onClick={handleLogout}
            className="flex items-center w-full py-2.5 px-3 hover:bg-red-500/10 rounded-xl transition-all duration-300 group text-white border border-red-500/20 hover:border-red-500/40"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <motion.div
              animate={{ x: [0, -2, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <FaSignOutAlt className="text-lg text-red-400 group-hover:text-red-300 transition-colors duration-300" />
            </motion.div>
            {(sidebarOpen || isHovering || isMobile) && !sidebarCollapsed && (
              <motion.span
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                className="ml-3 text-sm font-medium"
              >
                Cerrar Sesión
              </motion.span>
            )}
          </motion.button>
        </div>
      </motion.div>

      {/* Main Content Area - CORREGIDO: overflow controlado */}
      <motion.div 
        className="flex-1 flex min-w-0 w-full flex-col min-h-0 overflow-x-hidden" 
        style={{ marginLeft: getContentMargin() }}
        animate={{ marginLeft: getContentMargin() }}
        transition={{ type: "spring", damping: 30, stiffness: 200 }}
      >
        {/* Header */}
        <motion.header 
          className="bg-white/90 backdrop-blur-xl shadow-sm sticky top-0 z-40 border-b border-gray-200/70 flex-shrink-0"
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center justify-between px-4 py-3 md:px-5 md:py-3">
            <div className="flex items-center">
              <motion.button 
                onClick={handleSidebarToggle}
                aria-label={sidebarOpen ? 'Cerrar menú lateral' : 'Abrir menú lateral'}
                aria-expanded={sidebarOpen}
                className="text-[#0C4B45] hover:text-[#662D8F] focus:outline-none p-2 rounded-xl hover:bg-gradient-to-r hover:from-[#0C4B45]/5 hover:to-[#662D8F]/5 mr-3 transition-all duration-300"
                whileHover={{ scale: 1.1, rotate: 180 }}
                whileTap={{ scale: 0.9 }}
              >
                {sidebarOpen ? <FaTimes className="w-5 h-5" /> : <FaBars className="w-5 h-5" />}
              </motion.button>
              
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="flex items-center"
              >
                <h2 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-[#0C4B45] to-[#662D8F] bg-clip-text text-transparent">
                  {activeItemName}
                </h2>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                  className="ml-3 w-2 h-2 bg-gradient-to-r from-[#662D8F] to-[#F2A9FD] rounded-full"
                />
              </motion.div>
            </div>
            
            <div className="flex items-center space-x-2">
              {/* User Menu */}
              <div className="relative" ref={userMenuRef}>
                <motion.button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center space-x-2 bg-gradient-to-r from-[#0C4B45]/5 to-[#662D8F]/5 rounded-xl p-1.5 shadow-sm hover:shadow-md transition-all duration-300 border border-gray-200/50"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-[#662D8F] to-[#F2A9FD] flex items-center justify-center text-white text-sm font-bold shadow-sm">
                    {userInitial}
                  </div>
                  <div className="text-left hidden lg:block">
                    <p className="text-sm font-medium text-gray-800">{user?.name || 'Usuario'}</p>
                    <p className="text-xs text-[#662D8F] font-semibold">Administrador</p>
                  </div>
                  <motion.div
                    animate={{ rotate: userMenuOpen ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                    className="text-[#0C4B45]"
                  >
                    <FaChevronDown size={12} />
                  </motion.div>
                </motion.button>

                <AnimatePresence>
                  {userMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      className="absolute top-full right-0 mt-2 w-64 bg-white/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/50 z-50 overflow-hidden"
                    >
                      <div className="p-4 border-b border-gray-200/50">
                        <p className="text-sm font-semibold text-gray-900">{user?.name}</p>
                        <p className="text-xs text-gray-500">{user?.email}</p>
                      </div>
                      <div className="p-2">
                        
                        <button 
                          onClick={handleLogout}
                          className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-xl transition-all duration-300 flex items-center"
                        >
                          <FaSignOutAlt className="mr-2" size={12} />
                          Cerrar Sesión
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </motion.header>

        {/* Main Content - CORREGIDO: overflow controlado */}
        <motion.main 
          className="flex-1 overflow-y-auto p-4 md:p-5 bg-gradient-to-br from-transparent via-white/50 to-[#0C4B45]/5 min-h-0"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="w-full min-w-0 max-w-[1600px] mx-auto h-full">
            {/* Breadcrumbs */}
            <motion.div 
              className="flex items-center text-sm text-gray-600 mb-4 md:mb-5"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
            >
              <motion.span 
                whileHover={{ scale: 1.05 }}
                className="text-[#662D8F] font-semibold bg-gradient-to-r from-[#662D8F]/10 to-[#F2A9FD]/10 px-3 py-1 rounded-xl"
              >
                Admin Panel
              </motion.span>
              <motion.span 
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="mx-3 text-[#662D8F]"
              >
                •
              </motion.span>
              <span className="text-gray-800 font-medium">{activeItemName}</span>
            </motion.div>
            
            {/* Dynamic Content */}
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/50 overflow-hidden min-h-[calc(100vh-280px)]"
            >
              <Outlet />
            </motion.div>
          </div>
        </motion.main>
        
        {/* Footer */}
        <motion.footer 
          className="bg-white/80 backdrop-blur-xl border-t border-gray-200/50 p-4 text-center flex-shrink-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <p className="text-sm text-gray-600">
            © {new Date().getFullYear()} <span className="font-semibold bg-gradient-to-r from-[#0C4B45] to-[#662D8F] bg-clip-text text-transparent">AdminPcVasquez</span> - Panel de Administración 
          </p>
        </motion.footer>
      </motion.div>

      {/* Custom Scrollbar Styles */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(131, 244, 233, 0.1);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: linear-gradient(to bottom, #83F4E9, #662D8F);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(to bottom, #662D8F, #83F4E9);
        }
      `}</style>
    </div>
  );
};

export default AdminLayout;
